import { useState, type CSSProperties, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { del, post, put } from '../api/client';
import { qk, useSites } from '../api/queries';
import type { Robot, Site, SiteRef } from '../api/types';
import { useT } from '../i18n';
import { usePopoverClose } from '../lib/hooks';
import { SITE_COLORS, assignSite } from '../lib/sites';
import { toast, toastErr } from './toast';
import { Modal } from './ui';

/** The site's colour chip: "■ Музей". */
export function SiteBadge({ site, small, className, style }: { site: SiteRef; small?: boolean; className?: string; style?: CSSProperties }) {
  return (
    <span className={`site-badge${small ? ' sm' : ''} ${className ?? ''}`} style={{ color: site.color, background: `${site.color}1F`, borderColor: `${site.color}66`, ...style }} title={site.name}>
      <span className="sq" style={{ background: site.color }} aria-hidden />
      <span className="ellipsis">{site.name}</span>
    </span>
  );
}

/** The menu of sites (+ "no site"); `current` is ticked. */
function SiteMenuItems({ current, onPick, onManage }: { current: string | null | undefined; onPick: (id: string | null) => void; onManage?: () => void }) {
  const t = useT();
  const sites = useSites();
  return (
    <>
      <span className="section-label" style={{ padding: '2px 4px' }}>
        {t('site.robotSite')}
      </span>
      {(sites.data ?? []).map((s) => (
        <button key={s.id} type="button" role="menuitemradio" aria-checked={current === s.id} className={`menu-item site-item${current === s.id ? ' on' : ''}`} onClick={() => onPick(s.id)}>
          <span className="sq" style={{ background: s.color }} aria-hidden />
          <span className="grow ellipsis">{s.name}</span>
          {current === s.id && <span aria-hidden>✓</span>}
        </button>
      ))}
      <button type="button" role="menuitemradio" aria-checked={current === null} className="menu-item site-item muted" onClick={() => onPick(null)}>
        {t('site.none')}
      </button>
      {onManage && (
        <button type="button" role="menuitem" className="menu-item site-item muted" onClick={onManage}>
          {t('site.manage')}
        </button>
      )}
    </>
  );
}

/** Robot page header: the site chip; a click opens the menu to change it. */
export function RobotSiteChip({ robot }: { robot: Robot }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const ref = usePopoverClose(open, () => setOpen(false));
  const pick = async (id: string | null) => {
    setOpen(false);
    if ((robot.site?.id ?? null) === id) return;
    try {
      await assignSite([robot.serial], id);
    } catch (e) {
      toastErr(e);
    }
  };
  return (
    <span className="pop-anchor">
      <button type="button" className="site-chip-btn" aria-haspopup="menu" aria-expanded={open} title={t('site.change')} onClick={() => setOpen(!open)}>
        {robot.site ? <SiteBadge site={robot.site} /> : <span className="site-badge none">{t('site.noneShort')}</span>}
      </button>
      {open && (
        <div ref={ref} className="popover site-menu" role="menu" style={{ left: 0, top: 34 }}>
          <SiteMenuItems
            current={robot.site?.id ?? null}
            onPick={pick}
            onManage={() => {
              setOpen(false);
              setManage(true);
            }}
          />
        </div>
      )}
      {manage && <SitesModal onClose={() => setManage(false)} />}
    </span>
  );
}

/** Fleet bulk bar: "Move to a site…" for the selected robots. */
export function MoveToSiteButton({ serials, onDone }: { serials: string[]; onDone?: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = usePopoverClose(open, () => setOpen(false));
  const pick = async (id: string | null) => {
    setOpen(false);
    try {
      const res = await assignSite(serials, id);
      toast(res.site ? t('site.moved', { n: serials.length, site: res.site.name }) : t('site.movedNone', { n: serials.length }));
      onDone?.();
    } catch (e) {
      toastErr(e);
    }
  };
  return (
    <span className="pop-anchor">
      <button type="button" className="btn sm site-move" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        {t('site.moveTo')}
      </button>
      {open && (
        <div ref={ref} className="popover site-menu" role="menu" style={{ left: 0, top: 40 }}>
          <SiteMenuItems current={undefined} onPick={pick} />
        </div>
      )}
    </span>
  );
}

/** "Sites" dialog: the list with robot counts, rename / recolour, add, delete. */
export function SitesModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const sites = useSites();
  const [name, setName] = useState('');
  const [color, setColor] = useState(SITE_COLORS[0]!);
  const [edit, setEdit] = useState<Site | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.sites });
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await post('/api/sites', { name: name.trim(), color });
      setName('');
      setColor(SITE_COLORS[((sites.data?.length ?? 0) + 1) % SITE_COLORS.length]!);
      await refresh();
    } catch (err) {
      toastErr(err);
    }
  };
  const save = async () => {
    if (!edit) return;
    try {
      await put(`/api/sites/${edit.id}`, { name: edit.name.trim(), color: edit.color });
      setEdit(null);
      await refresh();
    } catch (err) {
      toastErr(err);
    }
  };
  const remove = async (s: Site) => {
    if (!window.confirm(t('site.deleteConfirm', { name: s.name, n: s.serials.length }))) return;
    try {
      await del(`/api/sites/${s.id}`);
      await refresh();
    } catch (err) {
      toastErr(err);
    }
  };
  const swatches = (cur: string, set: (c: string) => void) => (
    <div className="row" style={{ gap: 6 }} role="radiogroup" aria-label={t('site.color')}>
      {SITE_COLORS.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={cur === c} aria-label={c} className={`site-swatch${cur === c ? ' on' : ''}`} style={{ background: c }} onClick={() => set(c)} />
      ))}
    </div>
  );
  return (
    <Modal title={t('site.title')} onClose={onClose}>
      <div className="col" style={{ gap: 0 }}>
        <span className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          {t('site.oneSiteNote')}
        </span>
        {(sites.data ?? []).map((s) =>
          edit?.id === s.id ? (
            <div key={s.id} className="site-row editing">
              <input className="input sm" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} aria-label={t('site.name')} />
              {swatches(edit.color, (c) => setEdit({ ...edit, color: c }))}
              <div className="row" style={{ gap: 6 }}>
                <button type="button" className="btn sm primary" onClick={() => void save()}>
                  {t('common.save')}
                </button>
                <button type="button" className="btn sm ghost" onClick={() => setEdit(null)}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div key={s.id} className="site-row">
              <span className="sq" style={{ background: s.color }} aria-hidden />
              <b className="grow ellipsis">{s.name}</b>
              <span className="mono muted" style={{ fontSize: 12 }}>
                {t('site.robotsN', { n: s.serials.length })}
              </span>
              <button type="button" className="btn xs ghost" aria-label={t('site.edit', { name: s.name })} onClick={() => setEdit(s)}>
                ✎
              </button>
              <button type="button" className="btn xs ghost danger" aria-label={t('site.delete', { name: s.name })} onClick={() => void remove(s)}>
                ✕
              </button>
            </div>
          ),
        )}
        {!sites.data?.length && <span className="muted" style={{ fontSize: 13, padding: '8px 0' }}>{t('site.empty')}</span>}
        <form className="col site-add" style={{ gap: 10 }} onSubmit={add}>
          <label className="field">
            {t('site.new')}
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('site.newPlaceholder')} maxLength={64} />
          </label>
          <div className="row" style={{ gap: 10 }}>
            <span className="muted" style={{ fontSize: 12 }}>{t('site.color')}</span>
            {swatches(color, setColor)}
            <div className="grow" />
            <button type="submit" className="btn primary sm" disabled={!name.trim()}>
              {t('site.add')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

/** Robot pickers: "All from «Museum» · 15" buttons, one per site that has robots in `robots`. */
export function SiteQuickPicks({ robots, onPick }: { robots: Robot[]; onPick: (serials: string[]) => void }) {
  const t = useT();
  const bySite = new Map<string, { site: SiteRef; serials: string[] }>();
  for (const r of robots) {
    if (!r.site) continue;
    const e = bySite.get(r.site.id) ?? { site: r.site, serials: [] };
    e.serials.push(r.serial);
    bySite.set(r.site.id, e);
  }
  if (bySite.size < 1) return null;
  return (
    <div className="row wrap" style={{ gap: 6 }}>
      {[...bySite.values()]
        .sort((a, b) => a.site.name.localeCompare(b.site.name))
        .map(({ site, serials }) => (
          <button key={site.id} type="button" className="site-pick" style={{ color: site.color, borderColor: `${site.color}66`, background: `${site.color}1F` }} onClick={() => onPick(serials)}>
            {t('site.allFrom', { site: site.name, n: serials.length })}
          </button>
        ))}
    </div>
  );
}
